import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    FlexBox,
    FluidImage,
    Icon,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Typography,
} from '@vp/swan';
import { scrollToElementById } from '@99designs/browser';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { __image } from '@99designs/i18n';
import { sendExperimentClickTrackingEvent, usePage } from '@99designs/tracking';
import { CloudinaryImage } from '../CloudinaryImage';

export const DesignLiveExperiment = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inVariant = useInVistaExperimentVariant('design_services_hub_page_design_live');
    const { pageData } = usePage();

    return (
        <Card backgroundColor="strong" mb={{ md: 6, sm: 5, xs: 5 }}>
            <FlexBox
                alignItems="center"
                justifyContent="space-between"
                flexDirection={{ xs: 'column', sm: 'row' }}
                textAlign={{ xs: 'center', sm: 'left' }}
                gap={{ sm: 6, xs: 4 }}
            >
                <CloudinaryImage
                    render={(props) => (
                        <FluidImage
                            src={props.src}
                            srcSet={props.srcSet}
                            alt=""
                            style={{ minWidth: '220px', maxWidth: '220px' }}
                            role="presentation"
                        />
                    )}
                    imagePath={__image('expert_services/HubPage/design-live-EN-US')}
                />

                <FlexBox
                    style={{ flex: 'auto' }}
                    gap={{ sm: 6, xs: 5 }}
                    flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
                    alignItems={{ sm: 'start', md: 'center' }}
                >
                    <Box style={{ flex: '1 1 0%' }}>
                        <Typography fontSkin="title-subsection" mb={{ sm: 3, xs: 2 }}>
                            Collaborate live with a designer
                        </Typography>
                        <Typography fontSkin="body-standard" mt={2}>
                            Join a designer for a 30-minute live video call to create one product
                            design of your choice, including up to 3 post-session revisions to
                            perfect it,{' '}
                            <b>all for {inVariant('variant_10_dollar') ? '$10' : '$19'}</b>{' '}
                        </Typography>
                    </Box>
                    <Box style={{ flex: 'none' }}>
                        <Button
                            skin="primary"
                            onClick={() => {
                                setIsModalOpen(true);

                                const pagePath = pageData?.pagePath || window.location.pathname;
                                const pageZone = 'Design Live Card';

                                const eventDetail = `${pagePath};${pagePath};${pageZone};Schedule a video call`;
                                sendExperimentClickTrackingEvent({
                                    label: `${pageData.pageSection} view`,
                                    eventDetail: eventDetail,
                                    navigationDetail: 'Schedule a video call',
                                    pageSection: pageData.pageSection,
                                    pageStage: pageData.pageStage,
                                    pageName: pageData.pageName,
                                    experimentName: 'metric_testing_design_live_demand_experiment',
                                    variationName: inVariant('variant_10_dollar')
                                        ? 'variant_10_dollar'
                                        : 'variant_19_dollar',
                                });
                            }}
                        >
                            <Typography>Schedule a video call</Typography>
                            <Icon iconType="arrowRight" />
                        </Button>
                    </Box>
                </FlexBox>
                <FakedoorModal isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
            </FlexBox>
        </Card>
    );
};

interface FakedoorModalProps {
    isModalOpen: boolean;
    onClose: () => void;
}

export function FakedoorModal({ isModalOpen, onClose }: FakedoorModalProps) {
    const handleExploreOtherServicesClose = () => {
        onClose();
        scrollToElementById('design-categories-section', 75);
    };

    return (
        <ModalDialog isOpen={isModalOpen} onRequestDismiss={onClose}>
            <ModalDialogContent>
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText="Close" />
                </ModalDialogNav>
                <ModalDialogHeader mt={5}>
                    <ModalDialogTitle>Something we're working on</ModalDialogTitle>
                </ModalDialogHeader>
                <ModalDialogBody textAlign="left">
                    <Typography mb={5}>
                        We're currently exploring a new service to help you get professional designs
                        done faster. We're still in the early stages, but your interest helps us
                        know if we should build it.
                    </Typography>
                    <Typography>Thank you for your valuable feedback!</Typography>
                </ModalDialogBody>
                <ModalDialogFooter>
                    <Button
                        width="full-width"
                        skin="primary"
                        onClick={handleExploreOtherServicesClose}
                    >
                        Explore other services
                    </Button>
                </ModalDialogFooter>
            </ModalDialogContent>
        </ModalDialog>
    );
}
